import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  deleteOrbiConversation,
  loadMoreOrbiHistory,
  loadOrbiHistory,
  renameOrbiConversation,
  resumeConversation,
  selectOrbiHistoryCanLoadMore,
  selectOrbiHistoryConversations,
  selectOrbiHistoryLoading,
  selectOrbiSessionId,
} from '@patient-ui/patient-web/store';
import { OrbiHistoryConversation } from '@patient-ui/shared/models';
import {
  orbiHistoryDisplayTitle,
  orbiHistoryIsReadOnly,
  orbiHistoryToMessages,
} from '@patient-ui/shared/utils';

import { OrbiHistoryItemComponent } from '../../components/orbi-history-item/orbi-history-item.component';
import { OrbiAnnouncerService } from '../../services/orbi-announcer.service';

/**
 * Conversation-history view (PCSA-13169). Smart container shown inside the chat
 * panel when the user opens history: loads page 1 on init, renders the cards,
 * and lazily loads the next page on scroll (infinite scroll — no "load more"
 * button; PCSA-13312), auto-filling until the list overflows so pagination
 * never stalls when the first pages are short, then orchestrates resume /
 * rename / delete via the store.
 * Per design there is no separate header (the panel header stays) and no delete
 * confirmation; rename is an inline card editor owned by each item, which emits
 * the final title here. Emits `resumed` so the host can close the history view
 * once a conversation is reopened.
 */
@Component({
  selector: 'patient-ui-orbi-history-list',
  templateUrl: './orbi-history-list.component.html',
  styleUrls: ['./orbi-history-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiHistoryListComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  @Output() resumed = new EventEmitter<void>();

  /**
   * Whether the AI Agent is locked for the session by a locking guardrail
   * (PCSA-13170, mobile parity). While locked the list is browsable but every
   * NON-active conversation is grayed out + non-tappable, and the active
   * (read-only) one hides its options menu — so history cannot be used to resume
   * another chat and bypass the lock. Mirrors mobile `orbi_history_drawer.dart`.
   */
  @Input() sessionLocked = false;

  /** The scrollable list container — measured to auto-fill the viewport. */
  @ViewChild('list') private list?: ElementRef<HTMLElement>;

  @ViewChild('emptyState') private emptyState?: ElementRef<HTMLElement>;

  @ViewChildren(OrbiHistoryItemComponent)
  private rows?: QueryList<OrbiHistoryItemComponent>;

  readonly conversations$: Observable<OrbiHistoryConversation[]> =
    this.store.select(selectOrbiHistoryConversations);
  readonly loading$: Observable<boolean> = this.store.select(
    selectOrbiHistoryLoading
  );
  readonly activeSessionId$: Observable<string> =
    this.store.select(selectOrbiSessionId);

  /**
   * How close (px) to the bottom of the scroll area triggers the next page —
   * fires slightly before the true bottom so the next page is already loading as
   * the user reaches the end (infinite scroll, PCSA-13312).
   */
  private static readonly LOAD_MORE_THRESHOLD_PX = 120;

  /**
   * Latest can-load-more / loading snapshots, tracked so the (scroll) handler
   * can guard synchronously — only fetch the next page when there IS one and a
   * fetch isn't already in flight (prevents duplicate paginated requests).
   */
  private canLoadMore = false;
  private isLoading = false;
  /** Synchronous snapshot of the active conversation's id (for the resume guard). */
  private activeSessionId: string | null = null;
  private conversations: OrbiHistoryConversation[] = [];
  private awaitingRemoval: {
    sessionId: string;
    index: number;
    title: string;
  } | null = null;
  private focusRowAfterRemoval: number | null = null;
  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly announcer: OrbiAnnouncerService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(loadOrbiHistory());
    this.store
      .select(selectOrbiHistoryCanLoadMore)
      .pipe(takeUntil(this.destroyed))
      .subscribe((canLoadMore) => (this.canLoadMore = canLoadMore));
    this.store
      .select(selectOrbiHistoryLoading)
      .pipe(takeUntil(this.destroyed))
      .subscribe((loading) => (this.isLoading = loading));
    this.activeSessionId$
      .pipe(takeUntil(this.destroyed))
      .subscribe((id) => (this.activeSessionId = id));
    this.conversations$
      .pipe(takeUntil(this.destroyed))
      .subscribe((conversations) => this.onConversationsChanged(conversations));
  }

  /**
   * After each render, if the loaded pages don't fill the scroll area there is
   * nothing to scroll — so the (scroll) trigger would never fire and pagination
   * would stall (the bug that "only 6 show and can't scroll for more"). Keep
   * pulling the next page until the list overflows or the end is reached.
   */
  ngAfterViewChecked(): void {
    this.applyPendingFocus();

    const el = this.list?.nativeElement;
    if (el && this.canLoadMore && !this.isLoading && !this.isScrollable(el)) {
      this.store.dispatch(loadMoreOrbiHistory());
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  /** Whether the content overflows the container (i.e. there is room to scroll). */
  private isScrollable(el: HTMLElement): boolean {
    return el.scrollHeight > el.clientHeight;
  }

  private onConversationsChanged(
    conversations: OrbiHistoryConversation[]
  ): void {
    const removed = this.awaitingRemoval;
    this.conversations = conversations;
    if (
      !removed ||
      conversations.some((c) => c.metadata.sessionId === removed.sessionId)
    ) {
      return;
    }
    this.awaitingRemoval = null;
    if (this.holdsFocus()) {
      this.focusRowAfterRemoval = removed.index;
    }
    this.announcer.announce(`${removed.title} deleted`);
  }

  private holdsFocus(): boolean {
    const el = this.list?.nativeElement;
    return !!el && el.contains(el.ownerDocument.activeElement);
  }

  private applyPendingFocus(): void {
    const index = this.focusRowAfterRemoval;
    if (index === null) {
      return;
    }
    this.focusRowAfterRemoval = null;

    const rows = this.rows?.toArray() ?? [];
    for (let i = Math.min(index, rows.length - 1); i >= 0; i--) {
      if (rows[i].focusRow()) {
        return;
      }
    }
    const fallback = this.emptyState ?? this.list;
    fallback?.nativeElement.focus();
  }

  trackBySessionId(
    _index: number,
    conversation: OrbiHistoryConversation
  ): string {
    return conversation.metadata.sessionId;
  }

  /**
   * Infinite scroll (PCSA-13312): when the list is scrolled to within the
   * threshold of its bottom, load the next page. Guarded so it only fires when
   * another page exists and no fetch is already running.
   */
  onScroll(event: Event): void {
    if (!this.canLoadMore || this.isLoading) {
      return;
    }
    const el = event.target as HTMLElement;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= OrbiHistoryListComponent.LOAD_MORE_THRESHOLD_PX) {
      this.store.dispatch(loadMoreOrbiHistory());
    }
  }

  onResume(conversation: OrbiHistoryConversation): void {
    // While locked (PCSA-13170, mobile parity), only the ACTIVE conversation can
    // be reopened; every other row is disabled in the UI, and this guard makes
    // the no-op authoritative (a stray event cannot resume another chat and slip
    // the lock). The active read-only chat stays reopenable so the user can still
    // read it.
    if (
      this.sessionLocked &&
      conversation.metadata.sessionId !== this.activeSessionId
    ) {
      return;
    }
    this.store.dispatch(
      resumeConversation({
        sessionId: conversation.metadata.sessionId,
        messages: orbiHistoryToMessages(conversation),
        locked: orbiHistoryIsReadOnly(conversation),
        // The date this row shows, carried into the thread so the reopened
        // conversation is dated when it started rather than today (design review
        // 08/20). It is the only timestamp the service gives — the turns have
        // none — so it has to travel with the resume.
        startedAt: conversation.metadata?.createdAt ?? null,
      })
    );
    this.resumed.emit();
  }

  /** Inline rename saved on the item — dispatch the store update. */
  onRename(event: {
    conversation: OrbiHistoryConversation;
    title: string;
  }): void {
    this.store.dispatch(
      renameOrbiConversation({
        sessionId: event.conversation.metadata.sessionId,
        title: event.title,
      })
    );
  }

  /** Delete is immediate (design shows no confirmation dialog). */
  onDelete(conversation: OrbiHistoryConversation): void {
    const sessionId = conversation.metadata.sessionId;
    const index = this.conversations.findIndex(
      (c) => c.metadata.sessionId === sessionId
    );
    this.awaitingRemoval = {
      sessionId,
      index: Math.max(index, 0),
      title: orbiHistoryDisplayTitle(conversation),
    };
    this.store.dispatch(deleteOrbiConversation({ sessionId }));
  }
}
